import { Backdrop, CircularProgress } from "@mui/material";
import { useAlert } from "hooks/alerts/useAlert";
import useAddressValidation from "hooks/useAddressValidation";
import { useEffect, useRef, useState } from "react";
import { Address, AddressComponent, ValidateAddressRequest, ValidateAddressResponse } from "types/addressValidation";
import { getCountryName } from "utils/countries";
import AddressValidationDialog from "./AddressValidationDialog";

export type ValidatableAddress = {
  address: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
};

type UseAddressValidationFlowOptions = {
  address: ValidatableAddress;
  onAccepted: () => void;
  onAddressChange: (address: ValidatableAddress) => void;
  onMissingSubpremises?: () => void;
};

export default function useAddressValidationFlow({
  address,
  onAccepted,
  onAddressChange,
  onMissingSubpremises,
}: UseAddressValidationFlowOptions) {
  const { lastResponse, loading, request } = useAddressValidation();
  const { alertError } = useAlert();
  const [open, setOpen] = useState(false);

  const onAcceptedRef = useRef(onAccepted);
  const onAddressChangeRef = useRef(onAddressChange);
  const onMissingSubpremisesRef = useRef(onMissingSubpremises);
  onAcceptedRef.current = onAccepted;
  onAddressChangeRef.current = onAddressChange;
  onMissingSubpremisesRef.current = onMissingSubpremises;

  const validate = async () => {
    await request(toValidateAddressRequest(address));
  };

  const proceed = () => {
    setOpen(false);
    onAcceptedRef.current();
  };

  useEffect(() => {
    if (!lastResponse) return;
    const { result } = lastResponse;

    switch (result.verdict.possibleNextAction) {
      case "FIX":
        alertError("The address entered is not valid or incomplete. Please fix the address and try again.");
        break;

      case "CONFIRM":
        setOpen(true);
        break;

      case "CONFIRM_ADD_SUBPREMISES":
        setOpen(true);
        onMissingSubpremisesRef.current?.();
        alertError("The address may be missing a unit number. Please update and confirm the address.");
        break;

      case "ACCEPT":
        if (result.address.unconfirmedComponentTypes?.length) setOpen(true);
        else proceed();
        break;

      case "SKIP":
        proceed();
        break;

      default:
        break;
    }
  }, [lastResponse, alertError]);

  const ui = (
    <>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="secondary" size={96} />
      </Backdrop>
      {lastResponse && lastResponse.result.verdict.possibleNextAction !== "SKIP" && (
        <AddressValidationDialog
          open={open}
          address={address}
          formattedAddress={parseFormattedAddress(lastResponse)}
          validationResponse={lastResponse}
          onClose={() => setOpen(false)}
          onProceed={proceed}
          onFix={() => {
            setOpen(false);
            onMissingSubpremisesRef.current?.();
          }}
          onApplyFormattedAddress={() => onAddressChangeRef.current(parseFormattedAddress(lastResponse))}
        />
      )}
    </>
  );

  return { validate, loading, ui };
}

function parseFormattedAddress(validationResponse: ValidateAddressResponse): ValidatableAddress {
  const resultAddress = pickResultAddress(validationResponse);
  const postal = resultAddress.postalAddress;
  const components = resultAddress.addressComponents;
  const addressLines = postal?.addressLines?.filter(Boolean) ?? [];

  const streetNumber = componentNameByTypes(components, "street_number");
  const route = componentNameByTypes(components, "route");
  const street = [streetNumber, route].filter(Boolean).join(" ");
  const premise = componentNameByTypes(components, "premise");

  const address = addressLines[0] ?? (street || premise);
  const address2 =
    addressLines.length > 1
      ? addressLines.slice(1).join(", ")
      : componentNameByTypes(components, "subpremise", "floor", "room") || (street && premise ? premise : "");

  const city =
    postal?.locality ||
    postal?.sublocality ||
    componentNameByTypes(components, "locality", "postal_town", "sublocality", "sublocality_level_1", "neighborhood");

  const state = postal?.administrativeArea || componentNameByTypes(components, "administrative_area_level_1");

  const zipcode =
    [postal?.postalCode, postal?.sortingCode].filter(Boolean).join(" ") ||
    componentNameByTypes(components, "postal_code");

  const countryCode = postal?.regionCode || componentNameByTypes(components, "country");
  const country = (countryCode && getCountryName(countryCode)) || countryCode || "";

  return { address, address2, city, state, zipcode, country };
}

function pickResultAddress(validationResponse: ValidateAddressResponse): Address {
  const { address, englishLatinAddress } = validationResponse.result;
  if (!englishLatinAddress) return address;

  const latinPostal = englishLatinAddress.postalAddress;
  const hasUsableLatin = !!latinPostal?.addressLines?.length || !!latinPostal?.locality || !!latinPostal?.postalCode;
  return hasUsableLatin ? englishLatinAddress : address;
}

function componentNameByTypes(components: AddressComponent[] | undefined, ...types: string[]): string {
  if (!components?.length) return "";
  for (const type of types) {
    const match = components.find(({ componentType }) => componentType === type);
    const text = match?.componentName?.text?.trim();
    if (text) return text;
  }
  return "";
}

function toValidateAddressRequest(address: ValidatableAddress): ValidateAddressRequest {
  return {
    address: {
      addressLines: [
        address.address,
        address.address2,
        address.city,
        address.state,
        address.zipcode,
        address.country,
      ].filter(Boolean),
    },
    languageOptions: {
      returnEnglishLatinAddress: true,
    },
  };
}
